import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Breadcrumbs } from '~/chords/Breadcrumbs';
import { Button } from '~/components/Button';
import { Icon } from '~/components/Icon';
import { Pagination } from '~/components/Pagination/Pagination';
import { SearchBar } from '~/components/SearchBar/SearchBar';
import { Table } from '~/components/Table';
import { showToast } from '~/components/Toast';

import { useCampaigns } from './api/useCampaigns';
import { AboutAutomatedCreatorCampaignsModal } from './components/AboutAutomatedCreatorCampaignsModal/AboutAutomatedCreatorCampaignsModal';
import { AUTOMATED_CREATOR_CAMPAIGNS_INFO_MODAL_DISMISSED_KEY } from './constants';
import { useCampaignColumns } from './hooks/useCampaignColumns';

const PER_PAGE = 10;

const NEW_CAMPAIGN_PATH = '/marketing/automated-creator-campaigns/new';

export function AutomatedCreatorCampaignPage() {
  const { t } = useTranslation('marketing', {
    keyPrefix: 'automatedCreatorCampaign',
  });
  const navigate = useNavigate();

  const columns = useCampaignColumns();

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);

  const offset = (page - 1) * PER_PAGE;

  const { campaigns, totalCount, isFetching, isError } = useCampaigns({
    limit: PER_PAGE,
    offset,
    ...(searchTerm ? { track_name: searchTerm } : {}),
  });

  useEffect(() => {
    if (isError) {
      showToast({
        type: 'error',
        title: t('errors.fetchCampaignsTitle'),
        description: t('errors.fetchCampaignsDescription'),
      });
    }
  }, [isError, t]);

  const tableData = useMemo(
    () =>
      (campaigns ?? []).map((campaign) => ({
        ...campaign,
        id: String(campaign.id),
      })),
    [campaigns],
  );

  const total = totalCount ?? 0;

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleClear = () => {
    setSearchTerm('');
    setPage(1);
  };

  const handleStartNewCampaignClick = useCallback(() => {
    if (
      localStorage.getItem(
        AUTOMATED_CREATOR_CAMPAIGNS_INFO_MODAL_DISMISSED_KEY,
      ) === 'true'
    ) {
      navigate(NEW_CAMPAIGN_PATH);

      return;
    }

    setIsAboutModalOpen(true);
  }, [navigate]);

  const handleAboutModalClose = useCallback(() => {
    setIsAboutModalOpen(false);
  }, []);

  const handleAboutModalConfirm = useCallback(
    ({ dontShowAgain }: { dontShowAgain: boolean }) => {
      if (dontShowAgain) {
        localStorage.setItem(
          AUTOMATED_CREATOR_CAMPAIGNS_INFO_MODAL_DISMISSED_KEY,
          'true',
        );
      }

      setIsAboutModalOpen(false);
      navigate(NEW_CAMPAIGN_PATH);
    },
    [navigate],
  );

  return (
    <div className="p-xl">
      <Breadcrumbs.Root>
        <Breadcrumbs.Item to="/marketing">
          {t('breadcrumb.marketing')}
        </Breadcrumbs.Item>
        <Breadcrumbs.Item>{t('breadcrumb.current')}</Breadcrumbs.Item>
      </Breadcrumbs.Root>

      <div className="mt-md flex items-center justify-between">
        <h1 className="text-display-sm font-bold">{t('title')}</h1>
        <Button
          variant="primary"
          size="large"
          type="button"
          onClick={handleStartNewCampaignClick}
        >
          <Icon type="plus" size="sm" className="mr-xs" />
          {t('startNewCampaign')}
        </Button>
      </div>

      <AboutAutomatedCreatorCampaignsModal
        isOpen={isAboutModalOpen}
        onClose={handleAboutModalClose}
        onConfirm={handleAboutModalConfirm}
      />

      <div className="mt-xl flex items-end justify-between">
        <div>
          <h2 className="text-headline-sm font-semibold">
            {t('campaigns.heading', { count: total })}
          </h2>
          <p className="text-body-sm">{t('campaigns.subtitle')}</p>
        </div>
        <Pagination
          page={page}
          perPage={PER_PAGE}
          total={total}
          setPageNumber={setPage}
        />
      </div>

      <div className="mt-md">
        <SearchBar
          placeholder={t('campaigns.searchPlaceholder')}
          onSubmit={handleSearch}
          onClear={handleClear}
          formAriaLabel={t('campaigns.searchPlaceholder')}
          inputAriaLabel={t('campaigns.searchPlaceholder')}
        />
      </div>

      <div className="mt-md">
        <Table
          columns={columns}
          data={tableData}
          isLoading={isFetching}
          loadingRowCount={PER_PAGE}
          size="md"
          variant="striped"
        />
      </div>
    </div>
  );
}

AutomatedCreatorCampaignPage.displayName = 'AutomatedCreatorCampaignPage';
